import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockUsers } from '../utils/mockData';
import { ArrowLeft, Home, FileText, Calendar, DollarSign, Zap, User, Mail, Phone, Clock, ChevronRight, BadgeCheck, AlertTriangle, Clock3 } from 'lucide-react';
export const UserDetail = () => {
  const {
    userId
  } = useParams<{
    userId: string;
  }>();
  const navigate = useNavigate();
  const user = mockUsers.find(u => u.id === userId);
  if (!user) {
    return <div className="p-6" data-id="element-476">
        <div className="max-w-6xl mx-auto" data-id="element-477">
          <h1 className="text-2xl font-bold mb-6" data-id="element-478">User not found</h1>
          <button onClick={() => navigate('/')} className="flex items-center text-primary" data-id="element-479">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-480" />
            Back to Users
          </button>
        </div>
      </div>;
  }
  // Helper function to get finance status badge
  const getFinanceStatusBadge = (status: string) => {
    switch (status) {
      case 'pre-approved':
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800" data-id="element-481">
            <Clock3 className="h-3 w-3 mr-1" data-id="element-482" />
            Pre-approved
          </div>;
      case 'declined':
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800" data-id="element-483">
            <AlertTriangle className="h-3 w-3 mr-1" data-id="element-484" />
            Declined
          </div>;
      case 'finance accepted':
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-485">
            <BadgeCheck className="h-3 w-3 mr-1" data-id="element-486" />
            Accepted
          </div>;
      default:
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800" data-id="element-487">
            {status}
          </div>;
    }
  };
  // Helper function to get project status badge
  const getProjectStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800" data-id="element-488">
            <Clock className="h-3 w-3 mr-1" data-id="element-489" />
            Pending
          </div>;
      case 'accepted':
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800" data-id="element-490">
            <BadgeCheck className="h-3 w-3 mr-1" data-id="element-491" />
            Accepted
          </div>;
      case 'scheduled':
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800" data-id="element-492">
            <Calendar className="h-3 w-3 mr-1" data-id="element-493" />
            Scheduled
          </div>;
      case 'commissioned':
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800" data-id="element-494">
            <BadgeCheck className="h-3 w-3 mr-1" data-id="element-495" />
            Commissioned
          </div>;
      default:
        return <div className="flex items-center px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800" data-id="element-496">
            {status}
          </div>;
    }
  };
  return <div className="w-full min-h-screen bg-background" data-id="element-497">
      {/* Back navigation */}
      <div className="p-6 border-b" data-id="element-498">
        <div className="max-w-6xl mx-auto" data-id="element-499">
          <button onClick={() => navigate('/')} className="flex items-center text-primary" data-id="element-500">
            <ArrowLeft className="h-4 w-4 mr-2" data-id="element-501" />
            Back to Users
          </button>
        </div>
      </div>
      {/* Profile Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b" data-id="element-502">
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-id="element-503">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-504">
            <div className="flex items-center justify-center w-20 h-20 bg-primary/10 text-primary rounded-full" data-id="element-505">
              <User className="h-10 w-10" data-id="element-506" />
            </div>
            <div className="flex-1" data-id="element-507">
              <h1 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-508">
                {user.name}
              </h1>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-6 text-sm" data-id="element-509">
                <div className="flex items-center text-muted-foreground" data-id="element-510">
                  <Mail className="h-4 w-4 mr-2" data-id="element-511" />
                  {user.email}
                </div>
                <div className="flex items-center text-muted-foreground" data-id="element-512">
                  <Phone className="h-4 w-4 mr-2" data-id="element-513" />
                  {user.mobile}
                </div>
                <div className="flex items-center text-muted-foreground" data-id="element-514">
                  <Clock className="h-4 w-4 mr-2" data-id="element-515" />
                  Joined{' '}
                  {new Intl.DateTimeFormat('en-ZA', {
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit'
                }).format(user.signupDate)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-6xl mx-auto p-6" data-id="element-516">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-517">
          <div className="p-5 rounded-lg border bg-card shadow-sm hover:shadow-md transition-shadow" data-id="element-518">
            <div className="flex items-center justify-between mb-3" data-id="element-519">
              <span className="font-medium text-muted-foreground" data-id="element-520">
                Total Proposals
              </span>
              <div className="p-2 rounded-full bg-blue-100 text-blue-600" data-id="element-521">
                <FileText className="h-5 w-5" data-id="element-522" />
              </div>
            </div>
            <p className="text-3xl font-bold text-blue-600" data-id="element-523">
              {user.projects.length}
            </p>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-524">
              {user.projects.length === 1 ? 'Project' : 'Projects'} in total
            </p>
          </div>
          <div className="p-5 rounded-lg border bg-card shadow-sm hover:shadow-md transition-shadow" data-id="element-525">
            <div className="flex items-center justify-between mb-3" data-id="element-526">
              <span className="font-medium text-muted-foreground" data-id="element-527">
                Active Quotes
              </span>
              <div className="p-2 rounded-full bg-green-100 text-green-600" data-id="element-528">
                <Calendar className="h-5 w-5" data-id="element-529" />
              </div>
            </div>
            <p className="text-3xl font-bold text-green-600" data-id="element-530">
              {user.projects.filter(p => p.projectStatus === 'pending').length}
            </p>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-531">
              Pending approval
            </p>
          </div>
          <div className="p-5 rounded-lg border bg-card shadow-sm hover:shadow-md transition-shadow" data-id="element-532">
            <div className="flex items-center justify-between mb-3" data-id="element-533">
              <span className="font-medium text-muted-foreground" data-id="element-534">
                Total Investment
              </span>
              <div className="p-2 rounded-full bg-purple-100 text-purple-600" data-id="element-535">
                <DollarSign className="h-5 w-5" data-id="element-536" />
              </div>
            </div>
            <p className="text-3xl font-bold text-purple-600" data-id="element-537">R405,600</p>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-538">
              Across all projects
            </p>
          </div>
          <div className="p-5 rounded-lg border bg-card shadow-sm hover:shadow-md transition-shadow" data-id="element-539">
            <div className="flex items-center justify-between mb-3" data-id="element-540">
              <span className="font-medium text-muted-foreground" data-id="element-541">
                System Capacity
              </span>
              <div className="p-2 rounded-full bg-orange-100 text-orange-600" data-id="element-542">
                <Zap className="h-5 w-5" data-id="element-543" />
              </div>
            </div>
            <p className="text-3xl font-bold text-orange-600" data-id="element-544">20.5 kW</p>
            <p className="text-sm text-muted-foreground mt-1" data-id="element-545">
              Total installed
            </p>
          </div>
        </div>
        {/* Proposals Section */}
        <div data-id="element-546">
          <div className="flex items-center justify-between mb-6" data-id="element-547">
            <h2 className="text-2xl font-bold" data-id="element-548">Proposals</h2>
            <p className="text-sm text-muted-foreground" data-id="element-549">
              Manage and track solar installation proposals
            </p>
          </div>
          {/* Proposals List */}
          {user.projects.length > 0 ? <div className="space-y-6" data-id="element-550">
              {user.projects.map(project => <div key={project.id} className="group rounded-lg border bg-card shadow-sm hover:shadow-md hover:border-primary transition-all overflow-hidden cursor-pointer" onClick={() => navigate(`/users/${userId}/proposals/${project.id}`)} data-id="element-551">
                  <div className="flex justify-between items-center border-b p-4" data-id="element-552">
                    <div className="flex items-center" data-id="element-553">
                      <div className="p-2 mr-3 rounded-md bg-blue-50 text-blue-600" data-id="element-554">
                        <Home className="h-5 w-5" data-id="element-555" />
                      </div>
                      <div data-id="element-556">
                        <h3 className="font-semibold" data-id="element-557">
                          Residential Solar - {project.id}
                        </h3>
                        <p className="text-xs text-muted-foreground" data-id="element-558">
                          SQL-70396898-edd4
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-559">
                      {getFinanceStatusBadge(project.financeStatus)}
                      {getProjectStatusBadge(project.projectStatus)}
                    </div>
                  </div>
                  <div className="p-4 bg-card" data-id="element-560">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-561">
                      <div className="space-y-3" data-id="element-562">
                        <div data-id="element-563">
                          <p className="text-xs font-medium text-muted-foreground mb-1" data-id="element-564">
                            Address
                          </p>
                          <p className="font-medium" data-id="element-565">{project.address}</p>
                        </div>
                        <div data-id="element-566">
                          <p className="text-xs font-medium text-muted-foreground mb-1" data-id="element-567">
                            Finance Option
                          </p>
                          <p className="font-medium capitalize" data-id="element-568">
                            {project.financeOption}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-569">
                        <div data-id="element-570">
                          <p className="text-xs font-medium text-muted-foreground mb-1" data-id="element-571">
                            System Size
                          </p>
                          <p className="font-medium" data-id="element-572">20.5 kW</p>
                        </div>
                        <div data-id="element-573">
                          <p className="text-xs font-medium text-muted-foreground mb-1" data-id="element-574">
                            Investment
                          </p>
                          <p className="font-medium" data-id="element-575">R405,600</p>
                        </div>
                      </div>
                      <div className="space-y-3" data-id="element-576">
                        <div data-id="element-577">
                          <p className="text-xs font-medium text-muted-foreground mb-1" data-id="element-578">
                            Est. Savings
                          </p>
                          <p className="font-medium text-green-600" data-id="element-579">R486,720</p>
                        </div>
                        <div data-id="element-580">
                          <p className="text-xs font-medium text-muted-foreground mb-1" data-id="element-581">
                            Timeline
                          </p>
                          <p className="font-medium" data-id="element-582">
                            Jul 16, 2025 - Aug 15, 2025
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t flex items-center justify-between" data-id="element-583">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground" data-id="element-584">
                        <div className="flex items-center" data-id="element-585">
                          <span className="inline-block h-2 w-2 rounded-full bg-blue-400 mr-1" data-id="element-586"></span>
                          33 panels
                        </div>
                        <div className="flex items-center" data-id="element-587">
                          <span className="inline-block h-2 w-2 rounded-full bg-purple-400 mr-1" data-id="element-588"></span>
                          21.48kWh
                        </div>
                        <div className="flex items-center" data-id="element-589">
                          <span className="inline-block h-2 w-2 rounded-full bg-green-400 mr-1" data-id="element-590"></span>
                          100% offset
                        </div>
                      </div>
                      <div className="text-primary flex items-center text-sm font-medium group-hover:translate-x-0.5 transition-transform" data-id="element-591">
                        View Details <ChevronRight className="h-4 w-4 ml-1" data-id="element-592" />
                      </div>
                    </div>
                  </div>
                </div>)}
            </div> : <div className="p-12 text-center border rounded-lg bg-muted/20" data-id="element-593">
              <FileText className="h-10 w-10 text-muted-foreground mx-auto mb-4" data-id="element-594" />
              <h3 className="text-lg font-medium mb-2" data-id="element-595">No proposals found</h3>
              <p className="text-muted-foreground" data-id="element-596">
                This user hasn't created any proposals yet.
              </p>
            </div>}
        </div>
      </div>
    </div>;
};